// The Invoices tab (CHO-761): every ingested invoice per unit, filterable by
// unit and payment status, with the three dates that matter (invoice date,
// due date, paid date). Paying stays on /payments — this page is read-only.
//
// The page owns the single fetch (GET /api/invoices, paymentsReadAuth-gated)
// and all filter state; the table and filter bar are prop-driven components.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, authErrorMessage } from '../../lib/api';
import type { InvoiceListItem, InvoicePaymentStatus } from '../../types/invoices';
import { InvoicesTable } from '../../components/invoices/InvoicesTable';
import { InvoiceFilterBar, type UnitOption } from '../../components/invoices/InvoiceFilterBar';
import { propertyLabel } from '../../lib/invoices';

function unitKey(invoice: InvoiceListItem): string {
  return invoice.propertyId === null ? 'unassigned' : `p:${invoice.propertyId}`;
}

function errorMessage(err: unknown, prefix: string): string {
  return err instanceof Error ? `${prefix} (${err.message})` : prefix;
}

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Filter state as "deselected" sets: empty means everything selected, which
  // is the default and keeps units that appear on a later refresh visible.
  const [deselectedUnitKeys, setDeselectedUnitKeys] = useState<Set<string>>(new Set());
  const [deselectedStatuses, setDeselectedStatuses] = useState<Set<InvoicePaymentStatus>>(new Set());
  const [showExcluded, setShowExcluded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getInvoices();
      setInvoices(data.invoices);
    } catch (err) {
      // Same posture as the payments pages (CHO-759): an auth-class failure
      // (401 session expired, 429 sign-in lockout) names itself instead of
      // masquerading as an empty table.
      setError(
        authErrorMessage(err) ??
          errorMessage(err, 'Could not load invoices'),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const unitOptions = useMemo<UnitOption[]>(() => {
    const byKey = new Map<string, UnitOption>();
    for (const invoice of invoices) {
      const key = unitKey(invoice);
      if (!byKey.has(key)) byKey.set(key, { key, label: propertyLabel(invoice) });
    }
    return [...byKey.values()].sort((a, b) => {
      // "Unassigned" always sorts last so the named units read first.
      if (a.key === 'unassigned') return 1;
      if (b.key === 'unassigned') return -1;
      return a.label.localeCompare(b.label);
    });
  }, [invoices]);

  const toggleUnit = useCallback((key: string) => {
    setDeselectedUnitKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleStatus = useCallback((status: InvoicePaymentStatus) => {
    setDeselectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }, []);

  const filtered = useMemo(
    () =>
      invoices.filter(
        (invoice) =>
          !deselectedUnitKeys.has(unitKey(invoice)) &&
          !deselectedStatuses.has(invoice.paymentStatus) &&
          (showExcluded || !invoice.excluded),
      ),
    [invoices, deselectedUnitKeys, deselectedStatuses, showExcluded],
  );

  // Count what the toggle would actually reveal under the current filters.
  const excludedCount = useMemo(
    () =>
      invoices.filter(
        (invoice) =>
          invoice.excluded &&
          !deselectedUnitKeys.has(unitKey(invoice)) &&
          !deselectedStatuses.has(invoice.paymentStatus),
      ).length,
    [invoices, deselectedUnitKeys, deselectedStatuses],
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="text-sm text-gray-500 mt-1">
            Every invoice ingested from the bill inbox, per unit. Payments are initiated from{' '}
            <Link to="/payments" className="text-gray-700 underline underline-offset-2 hover:text-gray-900">
              Pay invoices
            </Link>
            .
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="px-3 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-100 disabled:opacity-40"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div role="alert" className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-6 text-center">
          <p className="text-sm font-medium text-red-800">{error}</p>
        </div>
      )}

      {!error && (
        <>
          <InvoiceFilterBar
            unitOptions={unitOptions}
            deselectedUnitKeys={deselectedUnitKeys}
            onToggleUnit={toggleUnit}
            deselectedStatuses={deselectedStatuses}
            onToggleStatus={toggleStatus}
            excludedCount={excludedCount}
            showExcluded={showExcluded}
            onToggleShowExcluded={() => setShowExcluded((v) => !v)}
          />
          {loading && invoices.length === 0 ? (
            <p className="text-gray-500 bg-white border border-gray-200 rounded-lg px-4 py-8 text-center">
              Loading invoices…
            </p>
          ) : (
            <InvoicesTable invoices={filtered} />
          )}
        </>
      )}
    </div>
  );
}
